/**
 * Report order — the order a match's reports read in on the club's
 * Insights Feed, the same for every match, most general first (Ben,
 * 2026-10-26). Staff see it; a platform admin moves packs up and down and
 * saves the whole order. Until one is saved the default stands
 * (insights_api.PACK_ORDER); a pack with no place reads after the
 * ordered ones, by title.
 */
import { useEffect, useState } from 'react'

import { getPackOrder, setPackOrder, type PackOrder } from '../packs/promotions'

export default function LabReportOrder({ canEdit = false }: { canEdit?: boolean }) {
  const [order, setOrder] = useState<PackOrder | null>(null)
  // the list being edited: every pack, ordered ones first (the server's
  // own arrangement) — saving places all of them
  const [names, setNames] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const take = (o: PackOrder) => { setOrder(o); setNames(o.packs.map((p) => p.name)) }
  useEffect(() => {
    getPackOrder().then(take).catch((e) => setMsg(`Could not load the order — ${String(e)}`))
  }, [])

  if (!order) return msg ? <div className="lab-error">{msg}</div> : null
  const title = (n: string) => order.packs.find((p) => p.name === n)?.title ?? n
  const saved = order.packs.map((p) => p.name)
  const changed = names.join('\n') !== saved.join('\n')
  const move = (i: number, by: -1 | 1) => setNames((xs) => {
    const j = i + by
    if (j < 0 || j >= xs.length) return xs
    const out = [...xs];
    [out[i], out[j]] = [out[j]!, out[i]!]
    return out
  })
  const save = async (list: string[], what: string) => {
    setBusy(true); setMsg(null)
    try {
      take(await setPackOrder(list, what))
      setMsg(list.length ? 'Saved — the Insights Feed reads in this order.'
                         : 'Back to the default order.')
    } catch (e) {
      setMsg(`Could not save — ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section data-testid="report-order">
      <div className="section-head">
        <h2>Report order</h2>
      </div>
      <p className="lab-sub" style={{ marginBottom: 'var(--space-2)' }}>
        The order a match&rsquo;s reports read in on the club&rsquo;s Insights
        Feed — the same for every match, most general first.{' '}
        {order.source === 'default'
          ? 'This is the default; no order has been saved.'
          : `Saved${order.updated_by ? ` by ${order.updated_by}` : ''}`
            + `${order.updated_at ? ` on ${order.updated_at.slice(0, 10)}` : ''}.`}
      </p>
      <ol className="lab-order">
        {names.map((n, i) => (
          <li key={n}>
            <span>{title(n)} <span className="muted mono">{n}</span></span>
            {canEdit && (
              <span className="lab-order__moves">
                <button type="button" disabled={busy || i === 0}
                        aria-label={`Move ${title(n)} up`} onClick={() => move(i, -1)}>↑</button>
                <button type="button" disabled={busy || i === names.length - 1}
                        aria-label={`Move ${title(n)} down`} onClick={() => move(i, 1)}>↓</button>
              </span>
            )}
          </li>
        ))}
      </ol>
      {canEdit && (
        <div className="lab-order__actions">
          <button type="button" className="ac-btn ac-btn--primary" disabled={busy || !changed}
                  onClick={() => void save(names, 'reorder reports')}>Save order</button>
          <button type="button" className="ac-btn" disabled={busy || order.source === 'default'}
                  onClick={() => {
                    if (window.confirm('Return to the default report order?')) void save([], 'reset to default')
                  }}>Reset to default</button>
        </div>
      )}
      {msg && <div className="lab-notice" role="status">{msg}</div>}
    </section>
  )
}
